import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import './UnitedNations.css';
import './AboutPages.css';

const FOCUS_AREAS_DATA = [
  {
    id: 'fa-1',
    title: 'Peace, Dialogue & Global Understanding',
    category: 'Humanitarian & Peace',
    icon: '🕊️',
    summary: 'Promoting dialogue, mutual respect, peaceful coexistence, and a deeper understanding among communities and cultures worldwide.',
    details: 'Peace is more than the absence of conflict; it is the presence of mutual respect, justice, and active dialogue. UNGDO works across international channels, academic institutions, and community forums to foster conflict resolution, cultural understanding, and global diplomacy.'
  },
  {
    id: 'fa-2',
    title: 'Human Rights Awareness & Education',
    category: 'Rights & Justice',
    icon: '⚖️',
    summary: 'Encouraging knowledge, awareness, and responsible engagement with fundamental human rights and human dignity.',
    details: 'Grounding community development in human rights ensures that every individual is respected regardless of background. UNGDO advocates for equal opportunity, freedom, civic responsibility, and informed participation in line with the UN Declaration of Human Rights.'
  },
  {
    id: 'fa-3',
    title: 'Education & Knowledge Development',
    category: 'Academic & Capacity',
    icon: '🎓',
    summary: 'Supporting learning, knowledge exchange, leadership development, and opportunities for intellectual and professional advancement.',
    details: 'Knowledge is the single most effective catalyst for individual and community transformation. UNGDO builds educational pathways, supports research, and facilitates academic exchange through initiatives like the UN University for Global Peace.'
  },
  {
    id: 'fa-4',
    title: 'Zero Hunger & Food Security',
    category: 'Humanitarian & Peace',
    icon: '🌾',
    summary: 'Encouraging awareness and initiatives that contribute to food security, nutrition, community resilience, and the reduction of hunger.',
    details: 'Ensuring access to nutritious food is fundamental to human dignity and community stability. UNGDO supports agricultural knowledge, local food security models, and emergency relief distribution in vulnerable areas.'
  },
  {
    id: 'fa-5',
    title: 'Health & Community Well-Being',
    category: 'Community & Health',
    icon: '🩺',
    summary: 'Supporting initiatives that promote healthier, more informed, and resilient communities.',
    details: 'Promoting preventive healthcare, mental health awareness, and civic wellness programs to ensure that individuals and families possess the vitality to actively engage in societal progress.'
  },
  {
    id: 'fa-6',
    title: 'Youth Empowerment & Leadership',
    category: 'Youth & Inclusion',
    icon: '🚀',
    summary: 'Creating pathways for young people to develop leadership, knowledge, confidence, and meaningful participation in society.',
    details: 'Young people are not merely beneficiaries of development—they are the active architects of tomorrow. UNGDO provides youth leadership forums, mentorship networks, and skill-building platforms to cultivate future global leaders.'
  },
  {
    id: 'fa-7',
    title: 'Women’s Empowerment & Inclusive Development',
    category: 'Youth & Inclusion',
    icon: '♀️',
    summary: 'Encouraging opportunity, participation, leadership, education, and inclusive social and economic advancement for women.',
    details: 'Societies achieve maximum prosperity when women are empowered as leaders, entrepreneurs, and decision-makers. UNGDO advocates for equal access to education, economic resources, and governance roles.'
  },
  {
    id: 'fa-8',
    title: 'Climate Action & Environmental Stewardship',
    category: 'Environmental',
    icon: '🌱',
    summary: 'Promoting environmental consciousness, responsible resource management, climate awareness, and sustainable practices.',
    details: 'Human progress and ecological health are fundamentally intertwined. UNGDO encourages sustainable resource utilization, climate resilience planning, renewable energy advocacy, and ecosystem conservation.'
  },
  {
    id: 'fa-9',
    title: 'Sustainable Cities & Communities',
    category: 'Environmental',
    icon: '🏙️',
    summary: 'Encouraging resilient, inclusive, and sustainable approaches to community and urban development.',
    details: 'Fostering civic infrastructure, green urban spaces, clean energy, and accessible community spaces that enhance safety, inclusivity, and long-term sustainability.'
  },
  {
    id: 'fa-10',
    title: 'Social & Economic Development',
    category: 'Economic & Prosperity',
    icon: '💼',
    summary: 'Supporting initiatives that encourage opportunity, self-reliance, skills development, entrepreneurship, and inclusive prosperity.',
    details: 'Building sustainable economic models through micro-enterprise support, vocational training, infrastructure funding, and fair market access to reduce poverty and generate community wealth.'
  },
  {
    id: 'fa-11',
    title: 'Humanitarian Engagement & Community Resilience',
    category: 'Humanitarian & Peace',
    icon: '🤝',
    summary: 'Supporting humanitarian-oriented initiatives and strengthening the capacity of communities to respond to social and developmental challenges.',
    details: 'Deploying targeted humanitarian assistance while equipping local leaders with tools to withstand economic shocks, natural disasters, and social crises with dignity and self-reliance.'
  },
  {
    id: 'fa-12',
    title: 'Inclusive Partnerships & Development Cooperation',
    category: 'Global Partnerships',
    icon: '🌐',
    summary: 'Facilitating collaboration among individuals, institutions, professionals, organizations, and development stakeholders in pursuit of shared objectives.',
    details: 'Complex global challenges demand collective action. UNGDO connects non-governmental organizations, academic institutions, civil society, and development donors into unified networks for global progress.'
  }
];

const CATEGORIES = ['All', 'Humanitarian & Peace', 'Rights & Justice', 'Academic & Capacity', 'Youth & Inclusion', 'Environmental', 'Economic & Prosperity', 'Global Partnerships'];

function AboutAreasOfFocus({ siteData }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCat, setSelectedCat] = useState('All');
  const [activeModalItem, setActiveModalItem] = useState(null);

  const filteredAreas = FOCUS_AREAS_DATA.filter((item) => {
    const matchesCat = selectedCat === 'All' || item.category === selectedCat;
    const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          item.summary.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCat && matchesSearch;
  });

  return (
    <div className="page-shell about-subpage">
      {/* Hero Banner */}
      <div className="about-hero">
        <span className="about-hero-pill">Areas of Engagement</span>
        <h1 className="about-hero-title">12 Core Areas of Focus</h1>
        <p className="about-hero-tagline">Addressing Contemporary Humanitarian, Social & Environmental Priorities</p>
        <p className="about-hero-desc">
          The United Nations Global Development Organization engages across a broad spectrum of development priorities, aligning local community action with global standards for peace, equality, and sustainability.
        </p>
      </div>

      {/* Navigation Bar Pills */}
      <div className="about-nav-pills">
        <NavLink to="/about" className="about-pill-btn">Overview & Mission</NavLink>
        <NavLink to="/about/areas-of-focus" className="about-pill-btn active">12 Areas of Focus</NavLink>
        <NavLink to="/about/strategic-pillars" className="about-pill-btn">Strategic Pillars & SDGs</NavLink>
        <NavLink to="/about/governance" className="about-pill-btn">Governance & Leadership</NavLink>
      </div>

      {/* Filter & Search Controls */}
      <div className="focus-filter-bar">
        <input
          type="text"
          className="focus-search-input"
          placeholder="🔍 Search focus areas (e.g., Youth, Peace, Climate, Education)..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          aria-label="Search focus areas"
        />
        <div className="focus-cats">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              type="button"
              className={`focus-cat-btn ${selectedCat === cat ? 'active' : ''}`}
              onClick={() => setSelectedCat(cat)}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Grid of 12 Focus Areas */}
      <div className="focus-grid">
        {filteredAreas.map((item) => (
          <div
            key={item.id}
            className="focus-card"
            onClick={() => setActiveModalItem(item)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setActiveModalItem(item);
              }
            }}
          >
            <div className="focus-card-top">
              <div className="focus-icon-box">{item.icon}</div>
              <span className="focus-badge">{item.category}</span>
            </div>
            <h3 className="focus-card-title">{item.title}</h3>
            <p className="focus-card-desc">{item.summary}</p>
            <div className="focus-card-footer">
              <span>Read Full Focus Details</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12 5 19 12 12 19"></polyline>
              </svg>
            </div>
          </div>
        ))}
      </div>

      {filteredAreas.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: '#64748b' }}>
          <h3>No focus areas match your search filter</h3>
          <p>Try searching for another keyword or reset the category filter.</p>
          <button type="button" className="about-pill-btn" onClick={() => { setSearchTerm(''); setSelectedCat('All'); }}>
            Reset Filters
          </button>
        </div>
      )}

      {/* Modal Popup for Deep Dive */}
      {activeModalItem && (
        <div
          className="team-modal-backdrop"
          onClick={() => setActiveModalItem(null)}
          role="dialog"
          aria-modal="true"
        >
          <div className="team-modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '640px' }}>
            <button
              type="button"
              className="team-modal-close-btn"
              onClick={() => setActiveModalItem(null)}
              aria-label="Close modal"
            >
              ✕
            </button>
            <div style={{ padding: '32px' }}>
              <div className="focus-card-top" style={{ marginBottom: '16px' }}>
                <div className="focus-icon-box" style={{ width: '56px', height: '56px', fontSize: '1.8rem' }}>{activeModalItem.icon}</div>
                <span className="focus-badge">{activeModalItem.category}</span>
              </div>
              <h2 style={{ color: '#0c2f4d', fontSize: '1.5rem', fontWeight: 800, margin: '0 0 12px' }}>
                {activeModalItem.title}
              </h2>
              <p style={{ color: '#0b3d91', fontWeight: 600, fontSize: '1rem', lineHeight: 1.6, marginBottom: '20px' }}>
                {activeModalItem.summary}
              </p>
              <div style={{ background: '#f8fafc', borderLeft: '4px solid #0b3d91', padding: '16px 20px', borderRadius: '0 8px 8px 0', marginBottom: '20px' }}>
                <h4 style={{ margin: '0 0 8px', color: '#0c2f4d', fontSize: '0.95rem' }}>Strategic Implementation & Impact</h4>
                <p style={{ margin: 0, color: '#475569', lineHeight: 1.7, fontSize: '0.95rem' }}>
                  {activeModalItem.details}
                </p>
              </div>
              <button
                type="button"
                className="about-cta-btn"
                style={{ width: '100%', justifyContent: 'center', margin: 0 }}
                onClick={() => setActiveModalItem(null)}
              >
                Close Focus Window
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Call to Action */}
      <div className="about-cta-banner">
        <h2 className="about-cta-title">Collaborate Across UNGDO Focus Areas</h2>
        <p className="about-cta-text">
          Whether in education, peacebuilding, youth affairs, or environmental action, UNGDO connects experts, communities, and institutions for measurable impact.
        </p>
        <NavLink to="/contact" className="about-cta-btn">
          Partner With UNGDO Head Office →
        </NavLink>
      </div>
    </div>
  );
}

export default AboutAreasOfFocus;
